// Core imports
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

// Component imports
import { Button, Checkbox, Col, Divider, Form, Input, Radio, Row, Select, Space, Spin, Tag, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import BotAvatar from '../UI/BotAvatar';
import MultiDropdown from './Fields/MultiDropdown';
import { LiveChatIcon } from '../Icons/LiveChatIcon';
import { GearIcon } from '../Icons/GearIcon';

// Misc imports
import { useFetchUserQuery, useInviteUserMutation, useUpdateUserMutation } from '../../store/users/usersApi';
import { ADDITIONAL_ROLES_ORG, PERMISSIONS_ORG, PERMISSIONS_TENANT } from '../../constants/main';
import { useFetchBotsQuery } from '../../store/bots/botsApi';
import { useFetchUserGroupsQuery } from '../../store/userGroups/userGroupsApi';
import useTheme from '../../utils/hooks/useTheme';

const OrganizationUserForm = ({
  bodyClassName,
  footerClassName,
  onCancel = () => {}
}) => {
  const { orgId } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { formProps } = useTheme();
  const location = useLocation();
  const [form] = Form.useForm();

  const isCreateMode = useMemo(() => {
    return searchParams.get('userId') === 'invite-new-user';
  }, [location]);

  const { data: user, isLoading: isFetchingUser } = useFetchUserQuery({ orgId, userId: searchParams.get('userId') }, { skip: !orgId || !searchParams.get('userId') || isCreateMode });
  const { data: bots, isLoading: isLoadingBots } = useFetchBotsQuery({ orgId }, { skip: !orgId || !searchParams.get('userId') });
  const { data: userGroups, isLoading: isLoadingUserGroups } = useFetchUserGroupsQuery({ orgId, urlParams: { pageSize: 500 } }, { skip: !orgId || isCreateMode });
  const { data: joinedUserGroups, isLoading: isLoadingJoinedUserGroups, refetch: refetchJoinedUserGroups } = useFetchUserGroupsQuery({ orgId, urlParams: { pageSize: 500, userId: searchParams.get('userId') }, delayed: true }, { skip: !orgId || !searchParams.get('userId') || isCreateMode });
  const [inviteUser, { isLoading: isInvitingUser }] = useInviteUserMutation();
  const [updateUser, { isLoading: isUpdatingUser }] = useUpdateUserMutation();

  const initialValues = useMemo(() => {
    return user ? {
      fullName: user.fullName,
      email: user.email,
      orgAccess: user?.organizationRoles[orgId]?.find(role => Object.values(PERMISSIONS_ORG).includes(role)) || '',
      appRoles: user?.organizationRoles[orgId]?.filter(role => Object.values(ADDITIONAL_ROLES_ORG).includes(role)),
      botIds: user?.tenantRoles[orgId] ? Object.entries(user?.tenantRoles[orgId] || {}).map(([botId, role]) => botId) : [],
      userGroupsIds: joinedUserGroups?.items?.map(group => group.id)
    } : {
      fullName: '',
      email: '',
      orgAccess: '',
      appRoles: [],
      botIds: [],
      userGroupsIds: []
    };
  }, [user, joinedUserGroups]);

  const [tenantRoles, setTenantRoles] = useState({});

  useEffect(() => {
    if (user) {
      setTenantRoles(user.tenantRoles[orgId] || {});
    }
  }, [user, userGroups]);

  useEffect(() => {
    if (user && joinedUserGroups) {
      form.resetFields();
    }
  }, [initialValues, form, user, joinedUserGroups]);

  useEffect(() => {
    return () => {
      form.resetFields();
    };
  }, []);

  const onSubmit = async (_values) => {
    const { fullName, email, orgAccess, appRoles = [], userGroupsIds } = form.getFieldsValue(true);

    let valuesToSubmit = {};

    if (user) {
      const organizationRoles = [
        ...appRoles,
        ...(user.organizationRoles?.[orgId] || []).filter(role => !Object.values(PERMISSIONS_ORG).includes(role) && !Object.values(ADDITIONAL_ROLES_ORG).includes(role))
      ];

      if (orgAccess) {
        organizationRoles.push(orgAccess);
      }

      valuesToSubmit = {
        organizationRoles: {
          [orgId]: organizationRoles
        },
        tenantRoles: {
          [orgId]: tenantRoles
        },
        groupIds: userGroupsIds
      };

      if (user.fullName !== fullName) {
        valuesToSubmit.fullName = fullName;
      }

      if (user.email !== email) {
        valuesToSubmit.email = email;
      }
    } else {
      const organizationRoles = [...appRoles];

      if (orgAccess) {
        organizationRoles.push(orgAccess);
      }

      valuesToSubmit = {
        fullName,
        email,
        organizationRoles: {
          [orgId]: organizationRoles
        },
        tenantRoles: {
          [orgId]: tenantRoles
        }
      };
    }

    let isSaveSuccessful = false;
    if (isCreateMode) {
      isSaveSuccessful = await inviteUser({ orgId, data: valuesToSubmit }).unwrap();

      if (isSaveSuccessful) {
        onCancel();
      }
    } else {
      const isUpdateSuccessfull = await updateUser({ orgId, userId: user.id, data: valuesToSubmit }).unwrap();

      if(isUpdateSuccessfull) {
        await refetchJoinedUserGroups();
      }
    }
  };

  return (
    <Fragment>
      <div className={bodyClassName}>
        {
          isFetchingUser || isLoadingBots || isLoadingUserGroups || isLoadingJoinedUserGroups ? (
            <Row justify="center">
              <Col className="p-5">
                <Spin />
              </Col>
            </Row>
          ) : (
            <Form
              form={form}
              layout="vertical"
              onFinish={onSubmit}
              initialValues={initialValues}
            >
              <Row
                gutter={16}
                className={`mb-4 ${formProps.flexDirection}`}
              >
                <Col xs={formProps.mainColSpan} className="mb-5">
                  <Typography.Paragraph className="mb-0">
                    {t('User Details')}
                  </Typography.Paragraph>

                  <Typography.Paragraph className="fs-sm mb-0" type="secondary" strong>
                    {t('Configure the user\'s details ')}
                  </Typography.Paragraph>
                </Col>

                <Col xs={formProps.secondaryColSpan}>
                  <Form.Item
                    hidden={isCreateMode}
                    name="fullName"
                    label={t('User Name')}
                  >
                    <Input
                      size="large"
                      placeholder={t('Enter a name for the user')}
                      disabled
                    />
                  </Form.Item>

                  <Form.Item
                    name="email"
                    label={t('Email')}
                    validateTrigger={['onChange', 'onBlur']}
                    rules={[{
                      required: isCreateMode,
                      type: 'email',
                      whitespace: true,
                      message: t('Please enter a valid email for this user')
                    }]}
                  >
                    <Input
                      size="large"
                      placeholder={t('Enter an email for the user')}
                      disabled={!isCreateMode}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Row
                wrap
                gutter={16}
                className="mb-4"
              >
                <Col xs={formProps.mainColSpan} className="mb-5">
                  <Typography.Paragraph className="mb-0">
                    {t('Organization Level Access')}
                  </Typography.Paragraph>

                  <Typography.Paragraph className="fs-sm mb-0" type="secondary" strong>
                    {t('Set organization access and permissions for the user')}
                  </Typography.Paragraph>
                </Col>

                <Col xs={formProps.secondaryColSpan}>
                  <Row align="top">
                    <Col span={12}>
                      <Form.Item
                        label={t('Access Roles')}
                        tooltip={{
                          className: 'ml-2',
                          icon: <QuestionCircleOutlined />,
                          title: t('Select the users\'s access level')
                        }}
                        name="orgAccess"
                        labelCol={{ span: 24 }}
                      >
                        <Radio.Group>
                          <Space direction="vertical">
                            <Radio value={''}>{t('No organization access')}</Radio>
                            <Radio value={PERMISSIONS_ORG.VIEWER}>{t('Organization viewer')}</Radio>
                            <Radio value={PERMISSIONS_ORG.EDITOR}>{t('Organization editor')}</Radio>
                            <Radio value={PERMISSIONS_ORG.ADMIN}>{t('Organization admin')}</Radio>
                          </Space>
                        </Radio.Group>
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item
                        name="appRoles"
                        tooltip={{
                          className: 'ml-2',
                          icon: <QuestionCircleOutlined />,
                          title: t('Add roles that grant permission to certain applications (e.g. LiveChat )')
                        }}
                        label={<Typography.Text>{t('Application Roles')}</Typography.Text>}
                        labelCol={{ span: 24 }}
                      >
                        <Checkbox.Group>
                          <Space direction="vertical">
                            <Checkbox value={ADDITIONAL_ROLES_ORG.LIVE_AGENT}>
                              <Tag className="fs-base border-radius-base px-2 py-1">
                                <LiveChatIcon className="fs-lg" />
                              </Tag>
                              {t('Live Agent')}
                            </Checkbox>

                            <Checkbox value={ADDITIONAL_ROLES_ORG.LIVECHAT_ADMIN}>
                              <Tag className="fs-base border-radius-base px-2 py-1">
                                <LiveChatIcon className="fs-lg"/>
                                <GearIcon className="fs-sm v-align-baseline" />
                              </Tag>
                              {t('LiveChat Admin')}
                            </Checkbox>
                          </Space>
                        </Checkbox.Group>
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>

              <Divider />

              <Row
                gutter={16}
                className={`mb-4 ${formProps.flexDirection}`}
              >
                <Col xs={formProps.mainColSpan} className="mb-5">
                  <Typography.Paragraph className="mb-0">
                    {t('Agent Level Access')}
                  </Typography.Paragraph>

                  <Typography.Paragraph className="fs-sm mb-0" type="secondary" strong>
                    {t('Set agent access and permissions for the user')}
                  </Typography.Paragraph>
                </Col>

                <Col xs={formProps.secondaryColSpan}>
                  <Form.Item
                    label={t('Access to Agents')}
                    name="botIds"
                  >
                    <MultiDropdown
                      size="large"
                      placeholder={t('Select Agents')}
                      allowClear
                      loading={isLoadingBots}
                      autoClearSearchValue={false}
                      hideExtraFromDropdown
                      onChange={(values) => {
                        let newTenantRoles = {};

                        // If a new bot has been added or edited, set it's role to the new one (or the default)
                        values.forEach((value) => {
                          newTenantRoles[value] = tenantRoles[value] || [PERMISSIONS_TENANT.VIEWER];
                        });

                        // If a bot has been removed, set it's role to null
                        Object.keys(tenantRoles).forEach((key) => {
                          if (!values.includes(key)) {
                            newTenantRoles[key] = null;
                          }
                        });

                        setTenantRoles(newTenantRoles);
                      }}
                      options={(bots?.items || []).map(bot => ({
                        label: <BotAvatar bot={bot} wrapBotName maxNameCharacters={28} className="d-inline-block w-auto" />,
                        value: bot.id,
                        searchValue: bot.displayName,
                        extra: (
                          <Select
                            value={Object.entries(tenantRoles).find(([botId, role]) => botId === bot.id)?.[1] || PERMISSIONS_TENANT.VIEWER}
                            className="ml-auto"
                            style={{ width: 100 }}
                            options={[
                              {
                                label: t('Viewer'),
                                value: PERMISSIONS_TENANT.VIEWER
                              },
                              {
                                label: t('Editor'),
                                value: PERMISSIONS_TENANT.EDITOR
                              },
                              {
                                label: t('Admin'),
                                value: PERMISSIONS_TENANT.ADMIN
                              }
                            ]}
                            onSelect={(value) => {
                              setTenantRoles({ ...tenantRoles, [bot.id]: [value] });
                            }}
                          />
                        )
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {
                !isCreateMode ? (
                  <Fragment>
                    <Divider />

                    <Row
                      wrap
                      gutter={16}
                      className="mb-4"
                    >
                      <Col xs={formProps.mainColSpan} className="mb-5">
                        <Typography.Paragraph className="mb-0">
                          {t('Member in User Groups')}
                        </Typography.Paragraph>

                        <Typography.Paragraph className="fs-sm mb-0" type="secondary" strong>
                          {t('Add console users to User Groups')}
                        </Typography.Paragraph>
                      </Col>

                      <Col xs={formProps.secondaryColSpan}>
                        <Form.Item
                          label={t('User Groups')}
                          name="userGroupsIds"
                        >
                          <MultiDropdown
                            size="large"
                            placeholder={t('Select User Groups')}
                            allowClear
                            loading={isLoadingUserGroups || isLoadingJoinedUserGroups}
                            autoClearSearchValue={false}
                            hideExtraFromDropdown
                            options={(userGroups?.items || []).map(userGroup => ({
                              label: <Typography.Text>{userGroup.name}</Typography.Text>,
                              value: userGroup.id,
                              searchValue: userGroup.name
                            }))}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Fragment>
                ) : null
              }
            </Form>
          )}
      </div>

      <div className={footerClassName}>
        <Row justify="end" gutter={8}>
          <Col>
            <Button
              size="large"
              onClick={onCancel}
              disabled={isInvitingUser || isUpdatingUser || isFetchingUser}
            >
              {t('Cancel')}
            </Button>
          </Col>

          <Col>
            <Button
              type="primary"
              size="large"
              className="d-block ml-auto"
              onClick={() => form.submit()}
              loading={isInvitingUser || isUpdatingUser || isFetchingUser}
            >
              {!isCreateMode ? t('Save Changes') : t('Invite User')}
            </Button>
          </Col>
        </Row>
      </div>
    </Fragment>
  );
};

OrganizationUserForm.propTypes = {
  bodyClassName: PropTypes.string,
  footerClassName: PropTypes.string,
  onCancel: PropTypes.func
};

export default OrganizationUserForm;
